import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { inkFor, minutesToHours } from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/misc';
import { formatHours, formatPercent, type FormatContext } from '@/lib/format';
import { Swatch } from '@/components/ui/swatch';

/** Fallback hue (validated categorical slot 1) for series that carry no colour of their own. */
export const SERIES_COLOR = '#2a78d6';
const GRID_COLOR = '#e5e7eb';
const TEXT_MUTED = '#52514e';

/** Fill plus the outline that keeps the mark readable against the card. */
export function markColors(d: Pick<Datum, 'color'>) {
  const fill = d.color ?? SERIES_COLOR;
  return { fill, stroke: inkFor(fill), strokeWidth: 1 };
}

export interface Datum {
  key: string;
  label: string;
  minutes: number;
  /** Optional swatch shown beside the label in the table (project colour). */
  color?: string;
}

interface ChartCardProps {
  title: string;
  /** Text alternative describing the chart (WCAG 1.1.1). */
  summary: string;
  data: Datum[];
  fmt: FormatContext;
  layout: 'horizontal' | 'vertical';
  total: number;
  emptyLabel: string;
  height?: number;
}

/**
 * Bar chart with a table alternative. Thin rounded marks, recessive grid, hover tooltip,
 * direct labels only at the tip (horizontal) or via tooltip (columns).
 *
 * A bar is filled with its datum's own colour where it has one — a project's colour, the same
 * one the time views draw its dot in — and outlined with {@link inkFor}, so the mark keeps a
 * boundary of at least 3:1 even when an admin picks a pale pastel. Identity never rests on
 * colour: every bar is named on the axis and repeated in the table view.
 */
export function ChartCard({
  title,
  summary,
  data,
  fmt,
  layout,
  total,
  emptyLabel,
  height = 260,
}: ChartCardProps) {
  const { t } = useTranslation();
  const [table, setTable] = useState(false);
  const id = useId();
  const rows = data.map((d) => ({ ...d, hours: minutesToHours(d.minutes) }));
  const hasData = rows.some((r) => r.minutes > 0);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle id={`${id}-title`}>{title}</CardTitle>
        {hasData ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTable((v) => !v)}
            aria-pressed={table}
            aria-controls={`${id}-body`}
          >
            {table ? t('dashboard.showChart') : t('dashboard.showTable')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent id={`${id}-body`}>
        {!hasData ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : table ? (
          <DataTable rows={rows} fmt={fmt} total={total} labelHeader={title} />
        ) : (
          <figure aria-labelledby={`${id}-title`} aria-describedby={`${id}-desc`}>
            <figcaption id={`${id}-desc`} className="sr-only">
              {summary}
            </figcaption>
            <div
              style={{
                height: layout === 'horizontal' ? Math.max(120, rows.length * 32 + 24) : height,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                {layout === 'horizontal' ? (
                  <BarChart
                    data={rows}
                    layout="vertical"
                    margin={{ top: 4, right: 56, bottom: 4, left: 4 }}
                    barCategoryGap={8}
                  >
                    <CartesianGrid horizontal={false} stroke={GRID_COLOR} strokeWidth={1} />
                    <XAxis type="number" hide domain={[0, 'dataMax']} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={140}
                      tick={{ fontSize: 12, fill: TEXT_MUTED }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      content={<HoursTooltip fmt={fmt} total={total} />}
                    />
                    <Bar
                      dataKey="hours"
                      maxBarSize={20}
                      radius={[0, 4, 4, 0]}
                      isAnimationActive={false}
                      label={{
                        position: 'right',
                        fontSize: 12,
                        fill: TEXT_MUTED,
                        // Non-breaking space: Recharts wraps SVG labels on spaces.
                        formatter: (v: number) =>
                          formatHours(Math.round(v * 60), fmt).replace(' ', ' '),
                      }}
                    >
                      {rows.map((r) => (
                        <Cell key={r.key} {...markColors(r)} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <BarChart
                    data={rows}
                    margin={{ top: 8, right: 8, bottom: 4, left: -16 }}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid vertical={false} stroke={GRID_COLOR} strokeWidth={1} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: TEXT_MUTED }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={16}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: TEXT_MUTED }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      content={<HoursTooltip fmt={fmt} total={total} />}
                    />
                    <Bar
                      dataKey="hours"
                      maxBarSize={24}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    >
                      {rows.map((r) => (
                        <Cell key={r.key} {...markColors(r)} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </figure>
        )}
      </CardContent>
    </Card>
  );
}

function HoursTooltip({
  active,
  payload,
  fmt,
  total,
}: {
  active?: boolean;
  payload?: { payload: Datum }[];
  fmt: FormatContext;
  total: number;
}) {
  const { t } = useTranslation();
  const d = payload?.[0]?.payload;
  if (!active || !d) return null;
  return (
    <div role="tooltip" className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{d.label}</div>
      <div className="tabular-nums">{formatHours(d.minutes, fmt)}</div>
      {total > 0 ? (
        <div className="text-muted-foreground">
          {t('dashboard.share')}: {formatPercent(d.minutes / total, fmt)}
        </div>
      ) : null}
    </div>
  );
}

export function DataTable({
  rows,
  fmt,
  total,
  labelHeader,
  extra,
}: {
  rows: Datum[];
  fmt: FormatContext;
  total: number;
  labelHeader: string;
  extra?: (d: Datum) => ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
            <th scope="col" className="py-1.5 pr-2 font-medium">
              {labelHeader}
            </th>
            {extra ? <th scope="col" className="py-1.5 pr-2 font-medium" /> : null}
            <th scope="col" className="py-1.5 pr-2 text-right font-medium">
              {t('app.hours')}
            </th>
            <th scope="col" className="py-1.5 text-right font-medium">
              {t('dashboard.share')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-b-0">
              <td className="py-1.5 pr-2">
                <span className="inline-flex items-center gap-2">
                  {r.color ? <Swatch color={r.color} className="h-2.5 w-2.5" /> : null}
                  {r.label}
                </span>
              </td>
              {extra ? <td className="py-1.5 pr-2 text-muted-foreground">{extra(r)}</td> : null}
              <td className="py-1.5 pr-2 text-right tabular-nums">{formatHours(r.minutes, fmt)}</td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {total > 0 ? formatPercent(r.minutes / total, fmt) : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
